import { z } from 'zod';

import { syncDeliveryErrorCodeValues } from '../sync/sync-delivery-errors.js';
import { holdingUnitEventEntityTypeValues } from '../holding-units/holding-unit-event-envelope.dto.js';
import { billingStatusValues, companyStatusValues } from '../tenancy/company.dto.js';
import { authErrorCodes } from './auth-error-codes.js';
import {
  authPrincipalTypeValues,
  authSessionPurposeValues,
  authSessionTypeValues,
  type AuthSessionClaimsDto,
  type AuthPrincipalType,
  type AuthSessionPurpose,
  type AuthSessionType,
  type AuthTokensResponse,
  type RevokeStationSyncSessionRequest,
  type RevokeStationSyncSessionResponse,
  type StationSyncPreflightIssue,
  type StationSyncPreflightRequest,
  type StationSyncPreflightResponse,
  type StationSyncRecheckKind,
  type StationSyncSessionRequest,
  type StationSyncSessionResponse,
  stationSyncRecheckKindValues,
} from './session.dto.js';

export const AuthSessionTypeSchema: z.ZodType<AuthSessionType> = z.enum(authSessionTypeValues);
export const AuthPrincipalTypeSchema: z.ZodType<AuthPrincipalType> =
  z.enum(authPrincipalTypeValues);
export const AuthSessionPurposeSchema: z.ZodType<AuthSessionPurpose> =
  z.enum(authSessionPurposeValues);

const BaseAuthSessionClaimsSchema = z
  .object({
    sessionType: AuthSessionTypeSchema,
    sessionId: z.uuid(),
    principalId: z.uuid(),
    companyId: z.uuid().optional(),
    authzVersion: z.number().int().nonnegative(),
  })
  .strict();

export const UserAuthSessionClaimsSchema = BaseAuthSessionClaimsSchema.extend({
  principalType: z.literal('user'),
  purpose: z.literal('interactive'),
  userId: z.uuid(),
}).strict();

export const StationAuthSessionClaimsSchema = BaseAuthSessionClaimsSchema.extend({
  principalType: z.literal('station'),
  purpose: z.literal('station_sync'),
  sessionType: z.literal('company'),
  companyId: z.uuid(),
  stationId: z.uuid(),
  deviceId: z.uuid(),
  deviceClaimId: z.uuid(),
  permissionProfileVersion: z.number().int().positive(),
}).strict();

export const AuthSessionClaimsSchema: z.ZodType<AuthSessionClaimsDto> = z.discriminatedUnion(
  'principalType',
  [UserAuthSessionClaimsSchema, StationAuthSessionClaimsSchema],
);

const BaseAuthTokensResponseSchema = z
  .object({
    accessToken: z.string().min(1),
    refreshToken: z.string().min(1),
    expiresInMs: z.number().int().positive(),
    sessionId: z.uuid(),
    principalId: z.uuid(),
    sessionType: AuthSessionTypeSchema,
    companyId: z.uuid().nullable(),
    authzVersion: z.number().int().nonnegative(),
  })
  .strict();

export const UserAuthTokensResponseSchema = BaseAuthTokensResponseSchema.extend({
  principalType: z.literal('user'),
  purpose: z.literal('interactive'),
  userId: z.uuid(),
}).strict();

export const StationAuthTokensResponseSchema = BaseAuthTokensResponseSchema.extend({
  principalType: z.literal('station'),
  purpose: z.literal('station_sync'),
  sessionType: z.literal('company'),
  companyId: z.uuid(),
  stationId: z.uuid(),
  deviceId: z.uuid(),
  deviceClaimId: z.uuid(),
  permissionProfileVersion: z.number().int().positive(),
}).strict();

export const AuthTokensResponseSchema: z.ZodType<AuthTokensResponse> = z.discriminatedUnion(
  'principalType',
  [UserAuthTokensResponseSchema, StationAuthTokensResponseSchema],
);

export const StationSyncSessionRequestSchema: z.ZodType<StationSyncSessionRequest> = z
  .object({
    stationId: z.uuid(),
    deviceId: z.uuid(),
    deviceClaimId: z.uuid(),
  })
  .strict();

export const StationSyncSessionResponseSchema: z.ZodType<StationSyncSessionResponse> =
  StationAuthTokensResponseSchema;

export const StationSyncRecheckKindSchema: z.ZodType<StationSyncRecheckKind> = z.enum(
  stationSyncRecheckKindValues,
);

export const StationSyncPreflightRequestSchema: z.ZodType<StationSyncPreflightRequest> = z
  .object({
    recheckKinds: z.array(StationSyncRecheckKindSchema).min(1).optional(),
    actorUserIds: z.array(z.uuid()).max(250).optional(),
    routeChecks: z
      .array(
        z
          .object({
            method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
            path: z.string().trim().startsWith('/v1/').max(512),
          })
          .strict(),
      )
      .max(100)
      .optional(),
    requiredFeatureKeys: z.array(z.string().trim().min(1).max(128)).max(100).optional(),
    contractChecks: z
      .array(
        z
          .object({
            eventType: z.string().trim().min(1).max(128),
            schemaVersion: z.number().int().positive(),
          })
          .strict(),
      )
      .max(100)
      .optional(),
    holdingUnitStreams: z
      .array(
        z
          .object({
            entityType: z.enum(holdingUnitEventEntityTypeValues),
            entityId: z.string().trim().min(1).max(200),
            expectedPredecessorVersion: z.number().int().nonnegative(),
          })
          .strict(),
      )
      .max(250)
      .optional(),
  })
  .strict();

export const StationSyncPreflightIssueSchema: z.ZodType<StationSyncPreflightIssue> = z
  .object({
    recheckKind: StationSyncRecheckKindSchema,
    errorCode: z.enum([...authErrorCodes, ...syncDeliveryErrorCodeValues]),
    message: z.string().trim().min(1).max(1024),
  })
  .strict();

export const StationSyncPreflightResponseSchema: z.ZodType<StationSyncPreflightResponse> = z
  .object({
    status: z.enum(['ready', 'blocked']),
    checkedAt: z.iso.datetime(),
    companyId: z.uuid(),
    stationId: z.uuid(),
    deviceId: z.uuid(),
    deviceClaimId: z.uuid(),
    permissionProfileVersion: z.number().int().positive(),
    issues: z.array(StationSyncPreflightIssueSchema),
    evidence: z
      .object({
        stationBindingCurrent: z.boolean(),
        permissionProfileCurrent: z.boolean(),
        routes: z.array(
          z
            .object({
              method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
              path: z.string(),
              allowed: z.boolean(),
              purpose: z.enum(['system', 'background', 'operator']).nullable(),
            })
            .strict(),
        ),
        features: z.array(z.object({ featureKey: z.string(), enabled: z.boolean() }).strict()),
        companyOperability: z
          .object({
            companyStatus: z.enum(companyStatusValues).nullable(),
            planCode: z.string().nullable(),
            billingStatus: z.enum(billingStatusValues).nullable(),
            planActive: z.boolean(),
            trialExpiresAt: z.iso.datetime().nullable(),
            operable: z.boolean(),
          })
          .strict(),
        contracts: z.array(
          z
            .object({
              eventType: z.string(),
              schemaVersion: z.number().int().positive(),
              supported: z.boolean(),
            })
            .strict(),
        ),
        actors: z.array(
          z
            .object({
              userId: z.uuid(),
              hasHistoricalMembership: z.boolean(),
            })
            .strict(),
        ),
        holdingUnitStreamHeads: z.array(
          z
            .object({
              entityType: z.enum(holdingUnitEventEntityTypeValues),
              entityId: z.string(),
              expectedPredecessorVersion: z.number().int().nonnegative(),
              cloudHeadVersion: z.number().int().nonnegative(),
              cloudLastEventId: z.uuid().nullable(),
              replayReady: z.boolean(),
            })
            .strict(),
        ),
      })
      .strict(),
  })
  .strict();

export const RevokeStationSyncSessionRequestSchema: z.ZodType<RevokeStationSyncSessionRequest> = z
  .object({
    reason: z.string().trim().min(1).max(512),
  })
  .strict();

export const RevokeStationSyncSessionResponseSchema: z.ZodType<RevokeStationSyncSessionResponse> = z
  .object({
    sessionId: z.uuid(),
    revokedAt: z.iso.datetime(),
    reason: z.string().trim().min(1).max(512),
  })
  .strict();
